/**
 * Client-side share-card rendering for Bilt build profiles.
 *
 * Everything is drawn with the browser canvas API at export resolution
 * (no server-side rendering available on shared hosting). Callers load
 * images themselves (see loadImageSafe) so rendering stays synchronous
 * apart from the final toBlob step.
 */

export type ShareCardFormat = 'story' | 'portrait' | 'square';

export interface ShareCardStat {
  label: string;
  value: string;
}

export type ShareCardHighlightKind = 'achievement' | 'award' | 'dyno' | 'personal_best';

export interface ShareCardHighlight {
  kind: ShareCardHighlightKind;
  /** Main line, e.g. achievement name, award title, or dyno result. */
  title: string;
  /** Supporting context, e.g. "Ranked #3 of 412" or venue and date. */
  detail?: string | null;
}

export interface ShareCardOptions {
  format: ShareCardFormat;
  /** Primary vehicle title, e.g. "2004 Subaru WRX STI". */
  title: string;
  /** Secondary line, e.g. a nickname. */
  subtitle?: string | null;
  ownerName?: string | null;
  /** Up to 4 stats are rendered; extras are ignored. */
  stats?: ShareCardStat[];
  highlight?: ShareCardHighlight | null;
  /** Already-loaded vehicle image. Pass null for a typographic card. */
  image?: HTMLImageElement | null;
  /** Short display URL, e.g. "biltspec.com/user/slug". */
  shortUrl: string;
  /** Already-loaded QR image; drawn small in the footer when provided. */
  qrImage?: HTMLImageElement | null;
  dateText?: string | null;
  /** e.g. "Verified result". Only drawn when provided. */
  verifiedLabel?: string | null;
}

export const SHARE_CARD_DIMENSIONS: Record<ShareCardFormat, { width: number; height: number }> = {
  story: { width: 1080, height: 1920 },
  portrait: { width: 1080, height: 1350 },
  square: { width: 1080, height: 1080 },
};

const FONT_STACK = "-apple-system, 'Helvetica Neue', Arial, sans-serif";

const COLORS = {
  bg: '#09090b',
  bgTop: '#141419',
  text: '#fafafa',
  muted: '#a1a1aa',
  dim: '#71717a',
  accent: '#f97316',
  surface: 'rgba(255,255,255,0.06)',
  surfaceBorder: 'rgba(255,255,255,0.12)',
};

const HIGHLIGHT_KIND_LABELS: Record<ShareCardHighlightKind, string> = {
  achievement: 'Achievement',
  award: 'Award',
  dyno: 'Dyno result',
  personal_best: 'Personal best',
};

/**
 * Loads an image with crossOrigin="anonymous" so it can be drawn to an
 * exportable canvas. Resolves null on load or CORS failure so callers can
 * degrade to a text-only card instead of throwing.
 */
export function loadImageSafe(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    if (!url) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function font(weight: number | string, size: number): string {
  return `${weight} ${Math.round(size)}px ${FONT_STACK}`;
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number
): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
      if (lines.length >= maxLines) {
        current = '';
        break;
      }
    } else {
      current = test;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);

  // Ellipsize the last line if it still overflows.
  const last = lines.length - 1;
  if (last >= 0) {
    let line = lines[last];
    if (ctx.measureText(line).width > maxWidth) {
      while (line.length > 1 && ctx.measureText(`${line}…`).width > maxWidth) {
        line = line.slice(0, -1);
      }
      lines[last] = `${line}…`;
    }
  }
  return lines;
}

function truncate(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let out = text;
  while (out.length > 1 && ctx.measureText(`${out}…`).width > maxWidth) {
    out = out.slice(0, -1);
  }
  return `${out}…`;
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

/** Draws an image cropped to cover the target rect (like CSS object-fit: cover). */
function drawImageCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number
) {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) return;
  const scale = Math.max(w / iw, h / ih);
  const sw = w / scale;
  const sh = h / scale;
  const sx = (iw - sw) / 2;
  // Bias the crop toward the upper part of the photo (cars are usually framed there).
  const sy = (ih - sh) * 0.4;
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

function drawBackground(ctx: CanvasRenderingContext2D, width: number, height: number) {
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, width, height);
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, COLORS.bgTop);
  gradient.addColorStop(0.55, COLORS.bg);
  gradient.addColorStop(1, '#060607');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
}

interface FooterLayout {
  /** Y coordinate where the footer region begins (content must stay above it). */
  top: number;
}

function drawFooter(
  ctx: CanvasRenderingContext2D,
  options: ShareCardOptions,
  width: number,
  height: number,
  margin: number
): FooterLayout {
  const qrSize = 132;
  const quiet = Math.max(12, Math.round(qrSize * 0.08));
  const qrPanel = qrSize + quiet * 2;
  const hasQr = Boolean(options.qrImage);
  const footerHeight = hasQr ? qrPanel : 112;
  const footerTop = height - margin - footerHeight;

  // Hairline separator above the footer.
  ctx.strokeStyle = COLORS.surfaceBorder;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(margin, footerTop - 36);
  ctx.lineTo(width - margin, footerTop - 36);
  ctx.stroke();

  const textMaxWidth = width - margin * 2 - (hasQr ? qrPanel + 40 : 0);

  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = COLORS.text;
  ctx.font = font(700, 46);
  ctx.fillText('BILT', margin, footerTop + 52);

  ctx.fillStyle = COLORS.muted;
  ctx.font = font(500, 30);
  ctx.fillText(truncate(ctx, options.shortUrl, textMaxWidth), margin, footerTop + 102);

  const metaParts: string[] = [];
  if (options.verifiedLabel) metaParts.push(options.verifiedLabel);
  if (options.dateText) metaParts.push(options.dateText);
  if (metaParts.length > 0) {
    ctx.fillStyle = options.verifiedLabel ? COLORS.accent : COLORS.dim;
    ctx.font = font(600, 24);
    const meta = truncate(ctx, metaParts.join('  ·  '), textMaxWidth);
    ctx.fillText(meta, margin, footerTop + 146 > height - margin ? footerTop + 138 : footerTop + 146);
  }

  if (hasQr && options.qrImage) {
    const panelX = width - margin - qrPanel;
    const panelY = height - margin - qrPanel;
    ctx.fillStyle = '#ffffff';
    roundRectPath(ctx, panelX, panelY, qrPanel, qrPanel, 16);
    ctx.fill();
    ctx.drawImage(options.qrImage, panelX + quiet, panelY + quiet, qrSize, qrSize);
  }

  return { top: footerTop - 36 };
}

interface ContentMetrics {
  height: number;
  titleLines: string[];
  highlightTitleLines: string[];
  highlightDetailLines: string[];
  statCount: number;
  titleSize: number;
  titleLineHeight: number;
}

function measureContent(
  ctx: CanvasRenderingContext2D,
  options: ShareCardOptions,
  width: number,
  margin: number,
  compact: boolean
): ContentMetrics {
  const innerWidth = width - margin * 2;
  const titleSize = compact ? 64 : 80;
  const titleLineHeight = titleSize * 1.1;

  ctx.font = font(800, titleSize);
  const titleLines = wrapText(ctx, options.title, innerWidth, compact ? 2 : 3);

  let total = 0;
  if (options.ownerName) total += 52;
  total += titleLines.length * titleLineHeight + 8;
  if (options.subtitle) total += compact ? 52 : 60;

  const statCount = Math.min(options.stats?.length ?? 0, 4);
  if (statCount > 0) total += compact ? 148 : 170;

  let highlightTitleLines: string[] = [];
  let highlightDetailLines: string[] = [];
  if (options.highlight) {
    const cardInner = innerWidth - 96;
    ctx.font = font(700, compact ? 36 : 42);
    highlightTitleLines = wrapText(ctx, options.highlight.title, cardInner, 2);
    if (options.highlight.detail) {
      ctx.font = font(400, compact ? 26 : 28);
      highlightDetailLines = wrapText(ctx, options.highlight.detail, cardInner, 2);
    }
    const cardHeight =
      64 +
      highlightTitleLines.length * (compact ? 44 : 52) +
      (highlightDetailLines.length > 0 ? highlightDetailLines.length * 38 + 8 : 0) +
      36;
    total += cardHeight + 36;
  }

  return {
    height: total,
    titleLines,
    highlightTitleLines,
    highlightDetailLines,
    statCount,
    titleSize,
    titleLineHeight,
  };
}

function drawContent(
  ctx: CanvasRenderingContext2D,
  options: ShareCardOptions,
  metrics: ContentMetrics,
  width: number,
  margin: number,
  startY: number,
  compact: boolean
) {
  const innerWidth = width - margin * 2;
  let y = startY;

  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';

  if (options.ownerName) {
    ctx.fillStyle = COLORS.muted;
    ctx.font = font(600, 26);
    const eyebrow = `BUILT BY ${options.ownerName.toUpperCase()}`;
    ctx.fillText(truncate(ctx, eyebrow, innerWidth), margin, y + 26);
    y += 52;
  }

  ctx.fillStyle = COLORS.text;
  ctx.font = font(800, metrics.titleSize);
  for (const line of metrics.titleLines) {
    y += metrics.titleLineHeight;
    ctx.fillText(line, margin, y);
  }
  y += 8;

  if (options.subtitle) {
    ctx.fillStyle = COLORS.muted;
    ctx.font = font(500, compact ? 34 : 38);
    y += compact ? 44 : 52;
    ctx.fillText(truncate(ctx, options.subtitle, innerWidth), margin, y);
    y += 8;
  }

  if (metrics.statCount > 0 && options.stats) {
    const stats = options.stats.slice(0, 4);
    y += compact ? 36 : 48;
    const cellWidth = innerWidth / stats.length;
    const valueSize = compact ? 48 : 54;

    stats.forEach((stat, i) => {
      const x = margin + i * cellWidth;
      if (i > 0) {
        ctx.strokeStyle = COLORS.surfaceBorder;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x, y + 4);
        ctx.lineTo(x, y + valueSize + 44);
        ctx.stroke();
      }
      const cellX = x + (i > 0 ? 28 : 0);
      const cellMax = cellWidth - (i > 0 ? 40 : 20);
      ctx.fillStyle = COLORS.text;
      ctx.font = font(700, valueSize);
      ctx.fillText(truncate(ctx, stat.value, cellMax), cellX, y + valueSize);
      ctx.fillStyle = COLORS.dim;
      ctx.font = font(600, 22);
      ctx.fillText(truncate(ctx, stat.label.toUpperCase(), cellMax), cellX, y + valueSize + 38);
    });
    y += valueSize + (compact ? 60 : 74);
  }

  if (options.highlight) {
    y += compact ? 28 : 36;
    const cardX = margin;
    const cardPad = 48;
    const titleLineHeight = compact ? 44 : 52;
    const cardHeight =
      64 +
      metrics.highlightTitleLines.length * titleLineHeight +
      (metrics.highlightDetailLines.length > 0
        ? metrics.highlightDetailLines.length * 38 + 8
        : 0) +
      36;

    ctx.fillStyle = COLORS.surface;
    roundRectPath(ctx, cardX, y, innerWidth, cardHeight, 24);
    ctx.fill();
    ctx.strokeStyle = COLORS.surfaceBorder;
    ctx.lineWidth = 2;
    roundRectPath(ctx, cardX, y, innerWidth, cardHeight, 24);
    ctx.stroke();

    // Accent bar on the left edge of the card.
    ctx.fillStyle = COLORS.accent;
    roundRectPath(ctx, cardX, y, 8, cardHeight, 4);
    ctx.fill();

    let cy = y + 52;
    ctx.fillStyle = COLORS.accent;
    ctx.font = font(700, 24);
    ctx.fillText(
      HIGHLIGHT_KIND_LABELS[options.highlight.kind].toUpperCase(),
      cardX + cardPad,
      cy
    );

    ctx.fillStyle = COLORS.text;
    ctx.font = font(700, compact ? 36 : 42);
    for (const line of metrics.highlightTitleLines) {
      cy += titleLineHeight;
      ctx.fillText(line, cardX + cardPad, cy);
    }

    if (metrics.highlightDetailLines.length > 0) {
      ctx.fillStyle = COLORS.muted;
      ctx.font = font(400, compact ? 26 : 28);
      cy += 8;
      for (const line of metrics.highlightDetailLines) {
        cy += 38;
        ctx.fillText(line, cardX + cardPad, cy);
      }
    }
  }
}

/**
 * Renders a share card to a high-resolution PNG blob entirely on the client.
 */
export async function renderShareCard(options: ShareCardOptions): Promise<Blob> {
  const { width, height } = SHARE_CARD_DIMENSIONS[options.format];
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is not supported in this browser');

  const margin = 72;
  const compact = options.format === 'square';

  drawBackground(ctx, width, height);
  const footer = drawFooter(ctx, options, width, height, margin);

  const metrics = measureContent(ctx, options, width, margin, compact);
  const contentGap = compact ? 48 : 64;

  const hasImage = Boolean(options.image);
  if (hasImage && options.image) {
    const maxImageHeight = Math.round(height * (options.format === 'story' ? 0.52 : 0.46));
    const minImageHeight = Math.round(height * 0.26);
    const available = footer.top - contentGap - metrics.height - contentGap;
    const imageHeight = Math.max(minImageHeight, Math.min(maxImageHeight, available));

    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, width, imageHeight);
    ctx.clip();
    drawImageCover(ctx, options.image, 0, 0, width, imageHeight);
    ctx.restore();

    // Scrim: fade the photo into the dark background so text stays readable.
    const scrim = ctx.createLinearGradient(0, imageHeight * 0.35, 0, imageHeight);
    scrim.addColorStop(0, 'rgba(9,9,11,0)');
    scrim.addColorStop(1, 'rgba(9,9,11,1)');
    ctx.fillStyle = scrim;
    ctx.fillRect(0, 0, width, imageHeight);

    const topScrim = ctx.createLinearGradient(0, 0, 0, imageHeight * 0.3);
    topScrim.addColorStop(0, 'rgba(0,0,0,0.35)');
    topScrim.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = topScrim;
    ctx.fillRect(0, 0, width, imageHeight * 0.3);

    drawContent(ctx, options, metrics, width, margin, imageHeight - 24, compact);
  } else {
    // Typographic layout: center the content block in the space above the footer.
    const available = footer.top - margin;
    const startY = margin + Math.max(40, (available - metrics.height) / 2 - 40);
    drawContent(ctx, options, metrics, width, margin, startY, compact);
  }

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/png')
  );
  if (!blob) throw new Error('Failed to export the share card image');
  return blob;
}
